import * as React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { faTimes } from '@fortawesome/sharp-regular-svg-icons';
import { assistanceIcon } from '@europrocurement/l2d-icons';
import {
    getCustomiserState,
    selectedPrescripteursSelector,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { FlexyButton } from '../../atoms/FlexyButton';
import { footerHeight } from '../../../assets';
import { AssistanceCard } from './AssistanceCard';
import { UserContactCardProps } from '../UserContactCard';

export type FooterAssistanceProps = {
    drawerContent?: React.ReactNode;
    onContactRequest?: (props: UserContactCardProps) => void;
    onFetchOffer?: (tiersId: number) => Promise<{ libelle: string } | undefined>;
    version?: string;
};

export const FooterAssistance: React.FunctionComponent<FooterAssistanceProps> = function ({
    drawerContent,
    onContactRequest,
    onFetchOffer,
    version = undefined,
}) {
    const { palette } = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLoadingOffer, setIsLoadingOffer] = React.useState(false);
    const [isEssentielOffer, setIsEssentielOffer] = React.useState(false);
    const ignoreNextClick = React.useRef(false);

    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);

    const handleClickOutside = React.useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const isInsideCard = target.closest('[data-assistance-card]');
        const isOnButton = target.closest('[data-assistance-button]');

        if (!isInsideCard && !isOnButton) {
            setIsOpen(false);
        }
    }, []);

    React.useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, handleClickOutside]);

    const handleToggle = async () => {
        if (ignoreNextClick.current) {
            ignoreNextClick.current = false;
            return;
        }

        if (isOpen) {
            setIsOpen(false);
            return;
        }

        if (!prescripteur?.id || !onFetchOffer) {
            setIsOpen(true);
            return;
        }

        setIsLoadingOffer(true);
        try {
            const offre = await onFetchOffer(prescripteur.id);
            setIsEssentielOffer(offre?.libelle?.toLowerCase().includes('essentiel') ?? false);
        } finally {
            setIsLoadingOffer(false);
            setIsOpen(true);
        }
    };

    const handleMouseDown = () => {
        if (isOpen) {
            ignoreNextClick.current = true;
        }
    };

    const closeIcon = {
        props: { icon: faTimes },
        name: 'close',
        displayName: 'Fermer',
    };

    return (
        <Box
            sx={{
                width: '100vw',
                height: `${footerHeight}px`,
                marginTop: '35px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
            }}
        >
            {isOpen && (
                <Box
                    data-assistance-wrapper="true"
                    sx={{
                        position: 'absolute',
                        bottom: '80px',
                        right: '90px',
                        zIndex: 2,
                    }}
                >
                    <AssistanceCard
                        onClose={() => setIsOpen(false)}
                        onContactRequest={onContactRequest}
                        isEssentielOffer={isEssentielOffer}
                    />
                </Box>
            )}

            <Box
                sx={{
                    width: '15vw',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'start',
                }}
            >
                {drawerContent}
            </Box>
            <Box
                sx={{
                    width: '85vw',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '20px',
                    }}
                >
                    <Typography
                        variant="caption"
                        color={palette.text.secondary}
                    >
                        © 2023 - {new Date().getFullYear()} Legal2Digital{' '}
                        {version ? `- v${version}` : ''} - Tous droits réservés
                    </Typography>
                    <Link
                        to="https://www.legal2digital.fr/cgv/"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            textDecoration: 'none',
                        }}
                    >
                        <Typography
                            variant="caption"
                            color={palette.text.secondary}
                            sx={{
                                '&:hover': { color: palette.text.primary },
                            }}
                        >
                            Conditions générales de vente
                        </Typography>
                    </Link>
                    <Link
                        to="https://www.legal2digital.fr/mentions-legales/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <Typography
                            variant="caption"
                            color={palette.text.secondary}
                            sx={{
                                '&:hover': { color: palette.text.primary },
                            }}
                        >
                            Mentions légales
                        </Typography>
                    </Link>
                    <Link
                        to="https://www.legal2digital.fr/confidentialite/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <Typography
                            variant="caption"
                            color={palette.text.secondary}
                            sx={{
                                '&:hover': { color: palette.text.primary },
                            }}
                        >
                            Confidentialité
                        </Typography>
                    </Link>
                    {getCustomiserState().env !== 'prod' ? (
                        <Link
                            to="/debug"
                            style={{ textDecoration: 'none' }}
                        >
                            <Typography
                                variant="caption"
                                color={palette.text.secondary}
                                sx={{
                                    '&:hover': { color: palette.text.primary },
                                }}
                            >
                                Debug
                            </Typography>
                        </Link>
                    ) : null}
                </Box>
            </Box>
            <Box
                sx={{
                    marginRight: '90px',
                }}
                onMouseDown={handleMouseDown}
            >
                <FlexyButton
                    label="Assistance"
                    icon={isOpen ? closeIcon : assistanceIcon}
                    type="button"
                    variant="contained"
                    size="large"
                    loading={isLoadingOffer}
                    sx={{
                        padding: '14px 20px 14px 20px',
                        borderRadius: '50px',
                        backgroundColor: isOpen ? palette.secondary.main : palette.primary.main,
                    }}
                    onClick={handleToggle}
                    data-assistance-button="true"
                />
            </Box>
        </Box>
    );
};
